import { AuPolicy, AuPolicyMap, AuScope } from './../oauth/internal';
import { AuResourceName } from './resources';

/**
 * For a given AuResource, create a different scope for each CRUD action
 */
export type DataResourceCrudScopes = {
  create?: AuScope;
  read?: AuScope;
  update?: AuScope;
  delete?: AuScope;
};

/**
 * Map from AuResourceName to the CrudScope
 */
export const DataResourceScopeMap: Record<
  AuResourceName,
  DataResourceCrudScopes
> = {
  users: {
    create: AuScope.UsersCreate,
    read: AuScope.UsersRead,
    update: AuScope.UsersUpdate,
    delete: AuScope.UsersDelete,
  },
  examples: {
    create: AuScope.ExamplesCreate,
    read: AuScope.ExamplesRead,
    update: AuScope.ExamplesUpdate,
    delete: AuScope.ExamplesDelete,
  },
  ['dashboard']: {
    create: AuScope.DashboardCreate,
    read: AuScope.DashboardRead,
    update: AuScope.DashboardUpdate,
    delete: AuScope.DashboardDelete,
  },
  ['conversion']: {
    create: AuScope.ConversionCreate,
    read: AuScope.ConversionRead,
    update: AuScope.ConversionUpdate,
    delete: AuScope.ConversionDelete,
  },
  ['conversion-filter']: {
    create: AuScope.ConversionFilterListCreate,
    read: AuScope.ConversionFilterListRead,
    update: AuScope.ConversionFilterListUpdate,
    delete: AuScope.ConversionFilterListDelete,
  },
  ['conversion-output-stream']: {
    create: AuScope.ConversionOutputStreamCreate,
    read: AuScope.ConversionOutputStreamRead,
    update: AuScope.ConversionOutputStreamUpdate,
    delete: AuScope.ConversionOutputStreamDelete,
  },
  ['conversion-output-stream-consumption-event']: {
    create: AuScope.ConversionOutputStreamConsumptionEventCreate,
    read: AuScope.ConversionOutputStreamConsumptionEventRead,
    update: AuScope.ConversionOutputStreamConsumptionEventUpdate,
    delete: AuScope.ConversionOutputStreamConsumptionEventDelete,
  },
  ['paid-source']: {
    create: AuScope.TrackingCreate,
    read: AuScope.TrackingRead,
    update: AuScope.TrackingUpdate,
    delete: AuScope.TrackingDelete,
  },
  ['tracked-call']: {
    create: AuScope.TrackingCreate,
    read: AuScope.TrackingRead,
    update: AuScope.TrackingUpdate,
    delete: AuScope.TrackingDelete,
  },
  ['tracked-url']: {
    create: AuScope.TrackingCreate,
    read: AuScope.TrackingRead,
    update: AuScope.TrackingUpdate,
    delete: AuScope.TrackingDelete,
  },
  ['funnel-stage']: {
    create: AuScope.FunnelCreate,
    read: AuScope.FunnelRead,
    update: AuScope.FunnelUpdate,
    delete: AuScope.FunnelDelete,
  },
  ['funnel-stage-assignment']: {
    create: AuScope.FunnelCreate,
    read: AuScope.FunnelRead,
    update: AuScope.FunnelUpdate,
    delete: AuScope.FunnelDelete,
  },
  ['retainer']: {
    create: AuScope.RetainerCreate,
    read: AuScope.RetainerRead,
    update: AuScope.RetainerUpdate,
    delete: AuScope.RetainerDelete,
  },
  ['custom-tracking-param']: {
    create: AuScope.TrackingCreate,
    read: AuScope.TrackingRead,
    update: AuScope.TrackingUpdate,
    delete: AuScope.TrackingDelete,
  },
  ['conversion-input-stream']: {
    create: AuScope.ConversionInputStreamCreate,
    read: AuScope.ConversionInputStreamRead,
    update: AuScope.ConversionInputStreamUpdate,
    delete: AuScope.ConversionInputStreamDelete,
  },
  ['ad-account']: {
    create: AuScope.AdAccountCreate,
    read: AuScope.AdAccountRead,
    update: AuScope.AdAccountUpdate,
    delete: AuScope.AdAccountDelete,
  },
  ['ad-data-stream']: {
    create: AuScope.AdDataStreamCreate,
    read: AuScope.AdDataStreamRead,
    update: AuScope.AdDataStreamUpdate,
    delete: AuScope.AdDataStreamDelete,
  },
  ['integration-connection']: {
    create: AuScope.IntegrationConnectionCreate,
    read: AuScope.IntegrationConnectionRead,
    update: AuScope.IntegrationConnectionUpdate,
    delete: AuScope.IntegrationConnectionDelete,
  },
};

/**
 * Scopes a policy grants.
 *
 * A policy missing from `AuPolicyMap` grants nothing instead of throwing.
 * Policies are persisted per user, so a value dropped from `AuPolicy` (e.g.
 * `zoho-admin`) outlives the code that knew it, and one stale row must not
 * break authorization everywhere it is checked.
 */
const scopesFor = (policy: AuPolicy): AuScope[] => AuPolicyMap[policy] ?? [];

/** Whether any of `policies` grants `scope`. */
export const hasScope = (policies: AuPolicy[], scope: AuScope): boolean =>
  policies.some((policy) => scopesFor(policy).includes(scope));

/**
 * Given an AuResourceName, a list of policies, and an action, check to see if
 * the list of policies contains the scope for the desired action on the given AuResource
 * @param auResourceName The AuResourceName
 * @param policies the list of policies to check
 * @param action the desired CRUD action
 * @returns whether or not the policies have correct scope for the given action
 */
export const checkAuthorization = (
  auResourceName: AuResourceName,
  policies: AuPolicy[],
  action: keyof DataResourceCrudScopes,
): boolean => {
  const desiredScope = DataResourceScopeMap[auResourceName][action];
  // An action with no scope mapped is never authorized.
  return desiredScope !== undefined && hasScope(policies, desiredScope);
};
